
import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Download, Package } from "lucide-react";
import { Agent } from "@/models/agent";
import { Button } from "@/components/ui/button";
import { AgentService } from "@/services/agent-service";
import { toast } from "@/components/ui/use-toast";

interface PurchasedAgentsProps {
  agents: Agent[];
}

const PurchasedAgents: React.FC<PurchasedAgentsProps> = ({ agents }) => {
  const handleDownload = async (agent: Agent) => {
    try {
      toast({
        title: "Downloading",
        description: "Starting agent download...",
      });
      const success = await AgentService.downloadAgent(agent.id);
      if (success) {
        toast({
          title: "Download Complete",
          description: "Agent downloaded successfully",
        });
      } else {
        toast({
          title: "Download Failed",
          description: "Unable to download the agent",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Download Error",
        description: "An error occurred while downloading the agent",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Purchased Agents</CardTitle>
        <CardDescription>Agents you've purchased or downloaded</CardDescription>
      </CardHeader>
      <CardContent>
        {agents.length === 0 ? (
          <div className="text-center py-8">
            <Package className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-2 text-lg font-medium">No agents yet</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              You haven't purchased any agents yet
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="flex items-center justify-between border-b pb-4 last:border-0 last:pb-0"
              >
                <div>
                  <h4 className="font-medium">{agent.name}</h4>
                  <p className="text-sm text-muted-foreground max-w-md">
                    {agent.description}
                  </p>
                  <div className="mt-1">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        agent.pricingModel === "free"
                          ? "bg-green-100 text-green-800"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {agent.pricingModel === "free"
                        ? "Free"
                        : `$${agent.price?.toFixed(2)}`}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1"
                  onClick={() => handleDownload(agent)}
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PurchasedAgents;
